import { ChangeEvent, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import toast, { Toaster } from "react-hot-toast";
import ContentLayout from "../components/ContentLayout";
import Datagrid from "../components/Datagrid/Datagrid";
import CreateEditDeleteButtonGroup from "../components/CreateEditDeleteButtonGroup";
import SearchInput from "../components/Datagrid/SearchInput";
import UserModal, { UserFormData } from "../components/Modal/UserModal";
import DeleteUserModal from "../components/Modal/DeleteUserModal";
import { BackendApi } from "../utils/BackendApi";
import { ColDef, SelectionChangedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import "bootstrap/dist/css/bootstrap.min.css";
import common from "../utils/common";
import { ApiRoute } from "../utils/ApiRoute";
import { useWindowSize } from "../hooks/useWindowSize";
import { AxiosResponse } from "axios";
import moment from "moment";
import { SystemMsg } from "../utils/SystemMsg";
import { RootState } from "../store/store";
import "bootstrap/dist/css/bootstrap.css";
import "./page.css";
import { useI18n } from "../i18n";

type UserListRow = {
  id: number;
  username: string;
  role: string;
  create_date: string;
  last_update_date: string;
};

function toUserListRow(item: unknown): UserListRow | null {
  if (!item || typeof item !== "object") {
    return null;
  }
  const row = item as Record<string, unknown>;
  const id = Number(row.id);
  if (!Number.isInteger(id)) {
    return null;
  }
  return {
    id,
    username: typeof row.username === "string" ? row.username : "",
    role: typeof row.role === "string" ? row.role : "",
    create_date: typeof row.create_date === "string" ? row.create_date : "",
    last_update_date:
      typeof row.last_update_date === "string" ? row.last_update_date : "",
  };
}

function dateRenderer(param: { value?: string }) {
  if (!param?.value) {
    return "";
  }
  const dateObj = moment(param.value);
  if (!dateObj.isValid()) {
    return "Invalid Date";
  }
  return dateObj.format(`YYYY-MM-DD HH:mm`);
}

const Title = () => {
  const { t } = useI18n();
  const title = t("nav.users") + " - " + t("meta.appName");
  return (
    <>
      <title>{title}</title>
    </>
  );
};

const Content = () => {
  const { height } = useWindowSize();
  const gridRef = useRef<AgGridReact>(null);
  const session = useSelector((state: RootState) => state.current.session);
  const [rowData, setRowData] = useState<UserListRow[]>();
  const [rowLoaded, setRowLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserFormData>();
  const [keyword, setKeyword] = useState("");
  const keywordRef = useRef(keyword);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(common.pageSizeArray[1]);
  const [paginationPage, setPaginationPage] = useState(0);

  const colDefs = [
    { field: "id", headerName: "ID", width: 80, sortable: true },
    { field: "username", headerName: "Username", flex: 2, sortable: true },
    { field: "role", headerName: "Role", width: 140, sortable: true },
    {
      field: "create_date",
      headerName: "Created",
      width: 200,
      sortable: true,
      cellRenderer: dateRenderer,
    },
    {
      field: "last_update_date",
      headerName: "Last Updated",
      flex: 1,
      minWidth: 200,
      sortable: true,
      cellRenderer: dateRenderer,
    },
  ] as ColDef<UserListRow>[];

  useEffect(() => {
    let cancelled = false;
    const query = keyword.trim();
    const keywordChanged = keywordRef.current !== keyword;
    keywordRef.current = keyword;
    const delay =
      keywordChanged && query.length > 0 ? common.DEBOUNCE_TIME : 0;

    const timer = window.setTimeout(() => {
      setRowLoaded(false);
      const params = query.length > 0 ? { keyword: query } : {};

      const onSuccess = (resp: AxiosResponse) => {
        if (cancelled) {
          return;
        }
        if (!Array.isArray(resp.data)) {
          toast.error(SystemMsg.User.failedToLoad);
          setRowData([]);
          setRowLoaded(true);
          return;
        }
        setRowData(
          resp.data
            .map(toUserListRow)
            .filter((row): row is UserListRow => row !== null),
        );
        setRowLoaded(true);
      };

      const onError = () => {
        if (cancelled) {
          return;
        }
        toast.error(SystemMsg.User.failedToLoad);
        setRowData([]);
        setRowLoaded(true);
      };

      BackendApi.get(ApiRoute.user, params, onSuccess, onError);
    }, delay);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [keyword, reloadKey]);

  const selectedRow = () => {
    if (selectedIds.length !== 1 || !rowData) {
      return undefined;
    }
    return rowData.find((row) => row.id === selectedIds[0]);
  };

  const onSelectionChanged = (event: SelectionChangedEvent) => {
    const nodes = event.selectedNodes;
    if (!Array.isArray(nodes) || nodes.length === 0) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(
      nodes
        .filter((node) => Number.isInteger(Number(node.data?.id)))
        .map((node) => Number(node.data.id)),
    );
  };

  const onCreateButtonClick = () => {
    setModalMode("add");
    setSelectedUser(undefined);
    setShowUserModal(true);
  };

  const onEditButtonClick = () => {
    const row = selectedRow();
    if (!row) {
      toast.error(SystemMsg.User.selectOne);
      return;
    }
    setModalMode("edit");
    setSelectedUser({ id: row.id, username: row.username, role: row.role });
    setShowUserModal(true);
  };

  const onDeleteButtonClick = () => {
    const row = selectedRow();
    if (!row) {
      toast.error(SystemMsg.User.selectOne);
      return;
    }
    if (
      session?.username &&
      row.username.toLowerCase() === session.username.toLowerCase()
    ) {
      toast.error(SystemMsg.User.cannotDeleteSelf);
      return;
    }
    setSelectedUser({ id: row.id, username: row.username, role: row.role });
    setShowDeleteModal(true);
  };

  const closeModals = () => {
    setShowUserModal(false);
    setShowDeleteModal(false);
    setSelectedIds([]);
    gridRef.current?.api?.deselectAll();
  };

  const pageCount = useMemo(() => {
    if (!rowData) {
      return 0;
    }
    return common.getPageCount(rowData.length, pageSize);
  }, [rowData, pageSize]);

  const gridOptions = useMemo(
    () => ({
      pagination: true,
      suppressPaginationPanel: true,
      paginationPageSize: pageSize,
    }),
    [pageSize],
  );

  useEffect(() => {
    if (!rowData || pageCount === 0 || page <= pageCount) {
      return;
    }
    setPage(pageCount);
    setPaginationPage(pageCount - 1);
  }, [rowData, page, pageCount]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || !rowLoaded) {
      return;
    }
    if (api.getGridOption("pagination") !== true) {
      api.setGridOption("pagination", true);
      api.setGridOption("suppressPaginationPanel", true);
    }
    if (api.paginationGetPageSize() !== pageSize) {
      api.setGridOption("paginationPageSize", pageSize);
    }
    const lastPage = Math.max(0, api.paginationGetTotalPages() - 1);
    const target = Math.min(paginationPage, lastPage);
    if (api.paginationGetCurrentPage() !== target) {
      api.paginationGoToPage(target);
    } 
  }, [pageSize, paginationPage, rowLoaded]);

  const clearSelection = () => {
    setSelectedIds([]);
    gridRef.current?.api?.deselectAll();
  };

  const onSearchChange = (value: string) => {
    setKeyword(value);
    setPage(1);
    setPaginationPage(0);
    clearSelection();
  };

  const handlePageClick = (event: { selected: number }) => {
    if (event?.selected == null || Number.isNaN(event.selected)) {
      return;
    }
    setPage(event.selected + 1);
    setPaginationPage(event.selected);
    clearSelection();
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextSize = Number(event?.target?.value);
    if (!Number.isInteger(nextSize) || nextSize <= 0) {
      return;
    }
    setPageSize(nextSize);
    setPage(1);
    setPaginationPage(0);
    clearSelection();
  };

  const getPageSizeArray = () => {
    const sizes = [...common.pageSizeArray];
    sizes.shift();
    return sizes;
  };

  const topLeftElement = (
    <SearchInput searchValue={keyword} setSearchValueFunc={onSearchChange} />
  );

  const topMenuElement = (
    <CreateEditDeleteButtonGroup
      onCreateButtonClick={onCreateButtonClick}
      onEditButtonClick={onEditButtonClick}
      onDeleteButtonClick={onDeleteButtonClick}
      disableEdit={selectedIds.length !== 1}
      disableDelete={selectedIds.length !== 1}
    />
  );

  return (
    <>
      <Datagrid
        rowSelect="singleRow"
        gridRef={gridRef}
        rowData={rowData}
        hasPagination
        colDefs={colDefs}
        customGridOptions={gridOptions}
        onSelectionChanged={onSelectionChanged}
        topLeftElement={topLeftElement}
        topRightElement={topMenuElement}
        hasControls={true}
        handlePageClick={handlePageClick}
        handlePageSizeChange={handlePageSizeChange}
        paginatePage={paginationPage}
        paginatePageCount={pageCount}
        paginatePageSizeArray={getPageSizeArray()}
        windowHeight={height}
        loading={!rowLoaded}
      />
      <UserModal
        toast={toast}
        mode={modalMode}
        show={showUserModal}
        data={selectedUser}
        onSaved={() => setReloadKey((key) => key + 1)}
        onClose={closeModals}
      />
      <DeleteUserModal
        toast={toast}
        show={showDeleteModal}
        data={selectedUser}
        onDeleted={() => setReloadKey((key) => key + 1)}
        onClose={closeModals}
      />
      <Toaster
        position="top-center"
        reverseOrder={false}
        toastOptions={{
          className: "",
          style: common.toasterStyle,
        }}
      />
    </>
  );
};

const Users = () => {
  return (
    <ContentLayout
      isFluid={true}
      maxWidth="80vw"
      header={<Title />}
      content={<Content />}
    />
  );
};

export default Users;
